import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Repeat, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { PrivacyConsent } from "./PrivacyConsent";
import { SIZE_INFO, SIZES } from "../lib/sizes";
import { loadDeliveryInfo, loadBuyerInfo } from "../lib/checkoutStorage";
import { useAuth } from "../contexts/AuthContext";
import { PER_ME_NOTICE } from "../lib/perMe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

export const SubscriptionDialog = ({ product, open, onOpenChange }) => {
  const { user } = useAuth();
  const d = loadDeliveryInfo() || {}; const b = loadBuyerInfo() || {};
  const [form, setForm] = useState({ size: "large", interval_weeks: 1, weekday: 4, delivery_name: user?.name || "", delivery_address: d.address || "", delivery_city: d.city || "",
    delivery_cap: d.cap || "", delivery_phone: b.phone || "", buyer_email: user?.email || b.email || "", delivery_time_slot: "mattina", privacy_consent: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  if (!product) return null;
  const base = product.display_price ?? product.price;
  const price = (size) => base * (1 + (SIZE_INFO[size]?.pct || 0) / 100);
  const missing = ["delivery_name", "delivery_address", "delivery_city", "delivery_phone", "buyer_email"].filter(k => !form[k].trim());

  const submit = async () => {
    if (missing.length) { toast.error("Compila tutti i campi di consegna"); return; }
    if (!form.privacy_consent) { toast.error("Accetta l'informativa privacy"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/per-me/subscriptions/checkout`, { ...form, product_id: product.id, buyer_name: form.delivery_name, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile avviare l'abbonamento"); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-forest flex items-center gap-2"><Repeat className="h-5 w-5 text-[#B85C38]" />Fiori freschi a casa</DialogTitle>
          <DialogDescription>{product.name}: lo ricevi a casa con la frequenza che scegli. Nessun vincolo, puoi sospendere quando vuoi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Ogni quanto?</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {[1, 2].map(w => <button type="button" key={w} onClick={() => setForm(p => ({ ...p, interval_weeks: w }))} className={`rounded-lg border px-3 py-2 text-sm ${form.interval_weeks === w ? "border-forest bg-forest/5 text-forest" : "border-cream-dark text-sage"}`} data-testid={`sub-interval-${w}`}>{w === 1 ? "Ogni settimana" : "Ogni due settimane"}</button>)}
            </div>
          </div>
          <div>
            <Label>Misura</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {SIZES.map(s => <button type="button" key={s} onClick={() => setForm(p => ({ ...p, size: s }))} className={`rounded-lg border px-2 py-2 text-xs ${form.size === s ? "border-forest bg-forest/5 text-forest" : "border-cream-dark text-sage"}`} data-testid={`sub-size-${s}`}><span className="block font-medium">{SIZE_INFO[s].label}</span>€{price(s).toFixed(2)}</button>)}
            </div>
          </div>
          <div>
            <Label>Giorno di consegna preferito</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">{DAYS.map((lbl, i) => <button type="button" key={i} onClick={() => setForm(p => ({ ...p, weekday: i }))} className={`rounded-full border px-3 py-1 text-xs ${form.weekday === i ? "border-forest bg-forest text-white" : "border-cream-dark text-sage"}`} data-testid={`sub-day-${i}`}>{lbl}</button>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome e cognome *</Label><Input value={form.delivery_name} onChange={set("delivery_name")} data-testid="sub-name" /></div>
            <div className="col-span-2"><Label>Indirizzo *</Label><Input value={form.delivery_address} onChange={set("delivery_address")} placeholder="Via e numero civico" data-testid="sub-address" /></div>
            <div><Label>Città *</Label><Input value={form.delivery_city} onChange={set("delivery_city")} data-testid="sub-city" /></div>
            <div><Label>CAP</Label><Input value={form.delivery_cap} onChange={set("delivery_cap")} data-testid="sub-cap" /></div>
            <div><Label>Telefono *</Label><Input value={form.delivery_phone} onChange={set("delivery_phone")} data-testid="sub-phone" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.buyer_email} onChange={set("buyer_email")} data-testid="sub-email" /></div>
          </div>
          <p className="text-xs text-sage leading-relaxed">{PER_ME_NOTICE}</p>
          <PrivacyConsent checked={form.privacy_consent} onChange={(v) => setForm(p => ({ ...p, privacy_consent: v }))} testId="sub-privacy" />
          <Button onClick={submit} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#9c4a2c] text-white py-6 text-base" data-testid="sub-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Attiva: €${price(form.size).toFixed(2)} ${form.interval_weeks === 1 ? "a settimana" : "ogni due settimane"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
